import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useMutation } from "react-query";
import axios from "axios";
import { useSelector } from "react-redux";

import Navbar from "../../Components/Navbar";
import tw from "tailwind-styled-components";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

import { Line } from "react-chartjs-2";

import {
  Bg,
  BoxContainer,
  Underline,
} from "../../Components/Styles/PageStyles";
import Images from "../../Images";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

const Revenue = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const ownerName = location?.state?.name || "Owner";
  const { id: clientId } = useParams();

  const user = useSelector((state) => state.UserReducer.user);

  const [revenueData, setRevenueData] = useState([]);

  const RevenueFunction = async () => {
    const body = new FormData();

    body.append("clientId", clientId);
    body.append("year", new Date().getFullYear());

    return await axios.post(`${Config.apiUrl}/getrevenuemonthly`, body, {
      headers: {
        authorization: "Bearer " + user.token,
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    });
  };

  const RevenueSuccess = (res) => {
    if (res?.data?.status === "OK") {
      setRevenueData(res?.data?.data || []);
    }
  };

  const RevenueError = (err) => {
    console.log(err);
  };

  const { isLoading, mutate: RevenueMutate } = useMutation(RevenueFunction, {
    onSuccess: RevenueSuccess,
    onError: RevenueError,
  });

  useEffect(() => {
    RevenueMutate();
  }, []);

  // ---------------- CHART DATA ----------------

  const chartData = useMemo(() => {
    return {
      labels: revenueData.map((item) => item.month),

      datasets: [
        {
          label: "Online",
          data: revenueData.map((item) => Number(item?.online || 0)),

          borderColor: "#3B82F6",
          backgroundColor: "rgba(59,130,246,0.1)",

          tension: 0.4,
          fill: true,
        },

        {
          label: "Cash",
          data: revenueData.map((item) => Number(item?.cash || 0)),

          borderColor: "#10B981",
          backgroundColor: "rgba(16,185,129,0.1)",

          tension: 0.4,
          fill: true,
        },
      ],
    };
  }, [revenueData]);

  // ---------------- OPTIONS ----------------

  const options = {
    responsive: true,
    maintainAspectRatio: false,

    interaction: {
      mode: "index",
      intersect: false,
    },

    plugins: {
      legend: {
        position: "top",
      },

      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ₹${context.raw.toLocaleString()}`;
          },

          afterBody: function (tooltipItems) {
            const dataIndex = tooltipItems[0].dataIndex;

            const total = Number(
              revenueData[dataIndex]?.total || 0
            );

            return [`Total: ₹${total.toLocaleString()}`]; 
          },
        },
      },
    },

    scales: {
      x: {
        grid: {
          display: false,
        },
      },

      y: {
        ticks: {
          callback: function (value) {
            return `₹${value.toLocaleString()}`;
          },
        },
      },
    },
  };

  const totalOnline = revenueData.reduce(
    (acc, curr) => acc + Number(curr.online || 0),
    0,
  );

  const totalCash = revenueData.reduce(
    (acc, curr) => acc + Number(curr.cash || 0),
    0,
  );

  const totalRevenue = totalOnline + totalCash;

  return (
    <Bg
      style={{
        backgroundImage: `url(${Images.BeamsBg})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        backgroundPosition: "top left",
        minHeight: "100vh",
      }}
    >
      <Navbar />

      <BoxContainer>
        <div className="flex justify-between items-center mb-8">
          <div>
            <Title>
              {t("Revenue")}
              <span>/{ownerName}</span>
            </Title>
            <Underline />
            <SubTitle>Online vs Cash Revenue Analytics</SubTitle>
          </div>
        </div>

        {isLoading ? (
          <Loading />
        ) : (
          <ChartCard>
            <div className="h-[400px]">
              <Line data={chartData} options={options} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <StatCard>
                <p className="text-gray-500 text-sm">Total Online</p>

                <h2 className="text-2xl font-bold text-blue-600">
                  ₹{totalOnline.toLocaleString()}
                </h2>
              </StatCard>

              <StatCard>
                <p className="text-gray-500 text-sm">Total Cash</p>
 
                <h2 className="text-2xl font-bold text-green-600">
                  ₹{totalCash.toLocaleString()}
                </h2>
              </StatCard>

              <StatCard>
                <p className="text-gray-500 text-sm">Total Revenue</p>

                <h2 className="text-2xl font-bold text-purple-600">
                  ₹{totalRevenue.toLocaleString()}
                </h2>
              </StatCard>
            </div>
          </ChartCard>
        )}
      </BoxContainer>
    </Bg>
  );
};

const Title = tw.h1`text-2xl font-semibold text-gray-800`;
const SubTitle = tw.p`text-sm text-gray-500 mt-1`;
const ChartCard = tw.div`bg-white rounded-2xl border border-gray-100 shadow-sm p-6`;
const StatCard = tw.div`bg-gray-50 rounded-xl p-4`;

export default Revenue;






























// import React, { useState, useMemo } from "react";
// import { useLocation } from "react-router-dom";
// import Navbar from "../../Components/Navbar";
// import tw from "tailwind-styled-components";
// import {
//   Chart as ChartJS,
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Tooltip,
//   Legend,
// } from "chart.js";

// import { Line } from "react-chartjs-2";
// import { Bg, BoxContainer } from "../../Components/Styles/PageStyles";
// import Images from "../../Images";

// ChartJS.register(
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Tooltip,
//   Legend,
// );

// const Revenue = () => {
//   const location = useLocation();

//   const ownerName = location?.state?.name || "Owner";

//   const [timeRange, setTimeRange] = useState("monthly");

//   // Dummy data
//   const monthlyData = [
//     { month: 1, year: 2026, online: 120000, cash: 40000 },
//     { month: 2, year: 2026, online: 140000, cash: 50000 },
//     { month: 3, year: 2026, online: 180000, cash: 70000 },
//     { month: 4, year: 2026, online: 160000, cash: 55000 },
//     { month: 5, year: 2026, online: 220000, cash: 90000 },
//   ];

//   const yearlyData = [
//     { year: 2023, online: 1200000, cash: 500000 },
//     { year: 2024, online: 1800000, cash: 800000 },
//     { year: 2025, online: 2400000, cash: 1000000 },
//     { year: 2026, online: 3200000, cash: 1400000 },
//   ];

//   const currentData = timeRange === "monthly" ? monthlyData : yearlyData;

//   const getMonthYearLabel = (month, year) => {
//     const date = new Date(year, month - 1, 1);

//     return date.toLocaleString("default", {
//       month: "short",
//       year: "2-digit",
//     });
//   };

//   const chartData = useMemo(() => {
//     if (timeRange === "monthly") {
//       return {
//         labels: currentData.map((item) =>
//           getMonthYearLabel(item.month, item.year),
//         ),

//         datasets: [
//           {
//             label: "Online",
//             data: currentData.map((item) => item.online),
//             borderColor: "#3B82F6",
//             backgroundColor: "rgba(59,130,246,0.1)",
//             tension: 0.4,
//             fill: true,
//           },

//           {
//             label: "Cash",
//             data: currentData.map((item) => item.cash),
//             borderColor: "#10B981",
//             backgroundColor: "rgba(16,185,129,0.1)",
//             tension: 0.4,
//             fill: true,
//           },
//         ],
//       };
//     }

//     return {
//       labels: currentData.map((item) => item.year),

//       datasets: [
//         {
//           label: "Online",
//           data: currentData.map((item) => item.online),
//           borderColor: "#3B82F6",
//           backgroundColor: "rgba(59,130,246,0.1)",
//           tension: 0.4,
//           fill: true,
//         },

//         {
//           label: "Cash",
//           data: currentData.map((item) => item.cash),
//           borderColor: "#10B981",
//           backgroundColor: "rgba(16,185,129,0.1)",
//           tension: 0.4,
//           fill: true,
//         },
//       ],
//     };
//   }, [timeRange]);

//   const options = {
//     responsive: true,
//     maintainAspectRatio: false,

//     plugins: {
//       legend: {
//         position: "top",
//       },

//       tooltip: {
//         callbacks: {
//           label: function (context) {
//             return `${context.dataset.label}: ₹${context.raw.toLocaleString()}`;
//           },
//         },
//       },
//     },

//     scales: {
//       x: {
//         grid: {
//           display: false,
//         },
//       },

//       y: {
//         ticks: {
//           callback: function (value) {
//             return `₹${value.toLocaleString()}`;
//           },
//         },
//       },
//     },
//   };

//   const totalOnline = currentData.reduce((acc, curr) => acc + curr.online, 0);

//   const totalCash = currentData.reduce((acc, curr) => acc + curr.cash, 0);

//   const totalRevenue = totalOnline + totalCash;

//   return (
//     <Bg
//       style={{
//         backgroundImage: `url(${Images.BeamsBg})`,
//         backgroundRepeat: "no-repeat",
//         backgroundSize: "contain",
//         backgroundPosition: "top left",
//         minHeight: "100vh",
//       }}
//     >
//       <Navbar />

//       <BoxContainer>
//         <div className="flex justify-between items-center mb-8">
//           <div>
//             <Title>{ownerName} Revenue</Title>

//             <SubTitle>Online vs Cash Revenue Analytics</SubTitle>
//           </div>

//           <div className="flex items-center bg-gray-100 rounded-full p-1">
//             {["monthly", "yearly"].map((range) => (
//               <button
//                 key={range}
//                 onClick={() => setTimeRange(range)}
//                 className={`px-4 py-2 text-sm rounded-full transition-all
//                   ${
//                     timeRange === range
//                       ? "bg-white shadow text-black"
//                       : "text-gray-500"
//                   }`}
//               >
//                 {range === "monthly" ? "Monthly" : "Yearly"}
//               </button>
//             ))}
//           </div>
//         </div>

//         <ChartCard>
//           <div className="h-[400px]">
//             <Line data={chartData} options={options} />
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
//             <StatCard>
//               <p className="text-gray-500 text-sm">Total Online</p>

//               <h2 className="text-2xl font-bold text-blue-600">
//                 ₹{totalOnline.toLocaleString()}
//               </h2>
//             </StatCard>

//             <StatCard>
//               <p className="text-gray-500 text-sm">Total Cash</p>

//               <h2 className="text-2xl font-bold text-green-600">
//                 ₹{totalCash.toLocaleString()}
//               </h2>
//             </StatCard>

//             <StatCard>
//               <p className="text-gray-500 text-sm">Total Revenue</p>

//               <h2 className="text-2xl font-bold text-purple-600">
//                 ₹{totalRevenue.toLocaleString()}
//               </h2>
//             </StatCard>
//           </div>
//         </ChartCard>
//       </BoxContainer>
//     </Bg>
//   );
// };

// const Title = tw.h1`
// text-2xl
// font-semibold
// text-gray-800
// `;

// const SubTitle = tw.p`
// text-sm
// text-gray-500
// mt-1
// `;

// const ChartCard = tw.div`
// bg-white
// rounded-2xl
// border
// border-gray-100
// shadow-sm
// p-6
// `;

// const StatCard = tw.div`
// bg-gray-50
// rounded-xl
// p-4
// `;

// export default Revenue;
